import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, CreditCard, FileSpreadsheet, Heart, Mail, MapPin, Target, Users, Clock, Check, ChevronDown } from 'lucide-react';
import SiteHeader from '../marketing/SiteHeader';
import SiteFooter from '../marketing/SiteFooter';
import AuthModal from '../AuthModal';
import { Markdown } from '../ui/markdown';
import { shotSrcSet } from './shots';
import { AuthModalContext, BrandButton, Highlight, Kicker, Paragraphs, Squiggle, assetUrl } from './ui';
import './home.css';

/*
 * Building blocks for the marketing sub-pages, measured from the Figma
 * "Functionaliteiten" frame (node 35748:358, 1800px wide). Same fluid
 * scale as the homepage: exact at 1800px, px/18 = vw below that.
 */

/* ── Page shell: header, footer, auth modal ─────────────────────────── */

export const PageShell = ({ children, testId }) => {
  const [auth, setAuth] = useState({ open: false, mode: 'register' });
  const openAuth = (mode) => setAuth({ open: true, mode });
  const location = useLocation();
  const { i18n } = useTranslation();

  // Admin preview links: <page>?lang=fr#section opens that language at that section
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('lang');
    if (requested && !i18n.language?.startsWith(requested)) i18n.changeLanguage(requested);
  }, [location.search, i18n]);

  useEffect(() => {
    if (!location.hash) return undefined;
    const timer = setTimeout(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(timer);
  }, [location.hash]);
  return (
    <AuthModalContext.Provider value={openAuth}>
      <div className="hp min-h-screen overflow-x-clip bg-white" data-testid={testId}>
        <SiteHeader />
        <main>{children}</main>
        <SiteFooter />
        <AuthModal
          isOpen={auth.open}
          onClose={() => setAuth((a) => ({ ...a, open: false }))}
          mode={auth.mode}
          onModeChange={(mode) => setAuth((a) => ({ ...a, mode }))}
        />
      </div>
    </AuthModalContext.Provider>
  );
};

/* ── Icons ──────────────────────────────────────────────────────────── */

/* 70px green circle with a white glyph (Figma card icons), or a step number. */
const FEAT_ICONS = ['chart', 'trend', 'pl', 'box', 'ai'];

export const FeatureIcon = ({ icon, number }) => {
  const cls = 'h-[clamp(52px,3.8889vw,70px)] w-[clamp(52px,3.8889vw,70px)] flex-shrink-0 rounded-full';
  if (number !== undefined || !FEAT_ICONS.includes(icon)) {
    return (
      <span className={`${cls} flex items-center justify-center bg-brand text-[clamp(20px,1.5556vw,28px)] font-bold text-white`}>
        {number ?? ''}
      </span>
    );
  }
  return <img src={`/brand/feat-icon-${icon}.svg`} alt="" aria-hidden className={cls} />;
};

/* 90px mint circle with a green line icon (Figma "Meer onder de motorkap"). */
const BOX_ASSETS = ['globe', 'invoice', 'shield'];
const BOX_LUCIDE = { bell: Bell, spreadsheet: FileSpreadsheet, card: CreditCard, users: Users, target: Target, heart: Heart, map: MapPin, mail: Mail, clock: Clock };
export const BOX_ICON_OPTIONS = [...BOX_ASSETS, ...Object.keys(BOX_LUCIDE)];

export const BoxIcon = ({ icon, size = 'lg' }) => {
  const box = size === 'lg' ? 'h-[clamp(64px,5vw,90px)] w-[clamp(64px,5vw,90px)]' : 'h-[clamp(52px,3.8889vw,70px)] w-[clamp(52px,3.8889vw,70px)]';
  const Lucide = BOX_LUCIDE[icon];
  return (
    <span className={`${box} flex flex-shrink-0 items-center justify-center rounded-full bg-brand-mint`}>
      {Lucide ? (
        <Lucide className="h-[48%] w-[48%] text-brand-ink" strokeWidth={1.75} />
      ) : (
        <img src={`/brand/box-icon-${BOX_ASSETS.includes(icon) ? icon : 'globe'}.svg`} alt="" aria-hidden className="h-[58%] w-[58%]" />
      )}
    </span>
  );
};

/* ── Hero (text only) ───────────────────────────────────────────────── */

export const PageHero = ({ hero, children }) => (
  <section className="hp-container pt-[clamp(48px,5.5vw,99px)] text-center" data-testid="page-hero">
    {hero.kicker && <Kicker className="text-[#273845]">{hero.kicker}</Kicker>}
    <h1 className="hp-display mx-auto mt-[clamp(18px,1.3333vw,24px)] max-w-[14.52em] text-[#273845]">
      <Highlight text={hero.title} />
    </h1>
    {hero.subtitle && (
      <p className="mx-auto mt-[clamp(14px,1.7222vw,31px)] max-w-[33.55em] text-[clamp(17px,1.3333vw,24px)] font-medium leading-[1.6667] text-[#273845]">
        {hero.subtitle}
      </p>
    )}
    {hero.updated && <p className="mt-3 text-[clamp(14px,0.8889vw,16px)] text-brand-muted">{hero.updated}</p>}
    {hero.cta_label && (
      <div className="mt-[clamp(20px,1.1667vw,21px)] flex justify-center">
        <BrandButton to={hero.cta_link || 'register'} data-testid="page-hero-cta">
          {hero.cta_label}
        </BrandButton>
      </div>
    )}
    {children}
  </section>
);

/* ── Squiggle + centred Outfit heading ──────────────────────────────── */

export const SectionIntro = ({ title, className = '' }) => (
  <div className={`hp-container ${className}`}>
    <Squiggle className="mt-[clamp(40px,5.4444vw,98px)]" />
    {title && (
      <h2 className="hp-h2-outfit mx-auto mt-[clamp(28px,3.2222vw,58px)] max-w-[15.42em] text-center text-[#273845]">
        <Highlight text={title} />
      </h2>
    )}
  </div>
);

/* ── Browser-window frame around a screenshot ───────────────────────── */

export const BrowserFrame = ({ src, alt, fit = 'contain', className = '' }) => (
  <div className={`hp-shot ${className}`}>
    <div className="relative flex h-[clamp(40px,3.5556vw,64px)] items-center justify-center border-b border-[#E5E5E5]">
      <img src="/brand/browser-dots.svg" alt="" aria-hidden className="absolute left-[clamp(16px,1.6667vw,30px)] h-[clamp(9px,0.7222vw,13px)] w-auto" />
      <span className="rounded-full bg-brand-mint px-[clamp(14px,1.6667vw,30px)] py-[clamp(4px,0.4444vw,8px)] text-[clamp(12px,1vw,18px)] font-medium leading-[20px] text-[#273845]">
        app.wowprofit.io
      </span>
    </div>
    {fit === 'cover' ? (
      <div className="relative aspect-[859/568] overflow-hidden bg-[#fafafa]">
        <img src={assetUrl(src)} srcSet={shotSrcSet(src)} sizes="(min-width: 1024px) 55vw, 100vw" alt={alt} loading="lazy" decoding="async" className="absolute left-0 top-0 h-auto w-full" />
      </div>
    ) : (
      <div className="bg-[#fafafa] px-[clamp(12px,1.5556vw,28px)] py-[clamp(20px,3.1111vw,56px)]">
        <img src={assetUrl(src)} srcSet={shotSrcSet(src)} sizes="(min-width: 1024px) 55vw, 100vw" alt={alt} loading="lazy" decoding="async" className="mx-auto block h-auto w-full" />
      </div>
    )}
  </div>
);

/* ── Dashed showcase card: icon, label, title, text, button + framed screenshot ── */

export const ShowcaseCard = ({ card, index, icon, kicker, children, id }) => {
  const imageLeft = index % 2 === 1;
  return (
    <div
      id={id}
      data-testid={`showcase-card-${index + 1}`}
      className={`hp-card hp-card-navy relative flex flex-col gap-10 px-5 py-8 sm:px-8 sm:py-10 lg:min-h-[min(937px,52.0556vw)] lg:flex-row lg:items-center lg:gap-0 lg:px-0 lg:py-[clamp(40px,5.7778vw,104px)] ${
        index % 2 === 1 ? 'bg-brand-mint' : 'bg-white'
      }`}
    >
      <div className={imageLeft ? 'lg:order-2 lg:ml-[4.0625%] lg:w-[41.375%] lg:flex-shrink-0' : 'lg:ml-[4%] lg:w-[41.375%] lg:flex-shrink-0'}>
        {icon}
        <Kicker className="mt-[clamp(20px,1.7778vw,32px)] text-brand-ink">{kicker ?? card.kicker}</Kicker>
        <h2 className="hp-h2 mt-[clamp(14px,1.1111vw,20px)] text-[#273845]">
          <Highlight text={card.title} />
        </h2>
        <div className="hp-body mt-[clamp(16px,1.7778vw,32px)] text-[#273845]">
          <Paragraphs text={card.body} />
        </div>
        {children}
        {card.cta_label && (
          <BrandButton to={card.cta_link || 'register'} className="mt-[clamp(24px,2.6667vw,48px)]">
            {card.cta_label}
          </BrandButton>
        )}
      </div>
      <BrowserFrame
        src={card.image_url}
        alt={card.kicker || card.title}
        fit={card.image_fit}
        className={
          imageLeft
            ? 'lg:order-1 lg:-ml-[3.125%] lg:w-[53.6875%] lg:flex-shrink-0'
            : 'lg:ml-[3.5%] lg:-mr-[2.5625%] lg:w-[53.6875%] lg:flex-shrink-0'
        }
      />
    </div>
  );
};

export const CardStack = ({ children, className = '' }) => (
  <div className={`hp-container mt-[clamp(28px,4vw,72px)] flex flex-col gap-[clamp(24px,3.3333vw,60px)] ${className}`}>{children}</div>
);

/* Check-list inside a card (used for the "how it works" tips). */
export const CheckList = ({ items }) =>
  items?.length ? (
    <ul className="mt-[clamp(16px,1.3333vw,24px)] space-y-2.5">
      {items.map((tip, i) => (
        <li key={i} className="flex items-start gap-3 text-[clamp(15px,1.1111vw,20px)] font-medium leading-[1.5] text-[#273845]">
          <img src="/brand/badge-check.svg" alt="" aria-hidden className="mt-[0.15em] h-[1.1em] w-[1.1em] flex-shrink-0" />
          {tip}
        </li>
      ))}
    </ul>
  ) : null;

/* ── Box grid ("Meer onder de motorkap") ────────────────────────────── */

export const BoxGrid = ({ kicker, title, subtitle, items, squiggle = true, blob = true, id }) => (
  <section id={id} className="relative isolate" data-testid="box-grid">
    {blob && (
      <img
        src="/brand/blob.svg"
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-[-5.3333vw] top-[clamp(40px,3.5vw,63px)] -z-10 h-[69.3889vw] w-[106vw] max-w-none select-none"
      />
    )}
    <div className="hp-container">
      {squiggle && <Squiggle className="mt-[clamp(32px,3.2222vw,58px)]" />}
      <div className={`text-center ${squiggle ? 'mt-[clamp(40px,8.6111vw,155px)]' : 'mt-[clamp(48px,5.3333vw,96px)]'}`}>
        {kicker && <Kicker className="mb-[clamp(18px,1.3333vw,24px)] text-[#273845]">{kicker}</Kicker>}
        <h2 className="hp-h2-bold mx-auto max-w-[13.99em] text-[#273845]">
          <Highlight text={title} />
        </h2>
        {subtitle && (
          <p className="mx-auto mt-[clamp(14px,1.3333vw,24px)] max-w-[38.5em] text-[clamp(16px,1.3333vw,24px)] font-medium leading-[1.5833] text-[#273845]">
            {subtitle}
          </p>
        )}
      </div>
      <div className="mt-[clamp(32px,4.0556vw,73px)] grid grid-cols-1 gap-6 sm:grid-cols-2 min-[900px]:grid-cols-3 lg:gap-[clamp(20px,2.7778vw,50px)]">
        {items.map((item, i) => (
          <div
            key={i}
            data-testid={`box-${i + 1}`}
            className={`${items.length % 2 === 1 && i === items.length - 1 ? 'sm:max-[899px]:col-span-2' : ''} rounded-[clamp(20px,1.6667vw,30px)] border border-[#DBDBDB] bg-white px-[clamp(20px,1.9444vw,35px)] pb-[clamp(24px,2.7778vw,50px)] pt-[clamp(24px,2.8889vw,52px)] lg:min-h-[min(493px,27.3889vw)]`}
          >
            <BoxIcon icon={item.icon} />
            <h3 className="mt-[clamp(14px,1.1111vw,20px)] text-[clamp(24px,2.2222vw,40px)] font-bold leading-[1.2] text-[#273845]">
              <Highlight text={item.title} />
            </h3>
            <p className="mt-[clamp(12px,1.5556vw,28px)] text-[clamp(16px,1.3333vw,24px)] font-medium leading-[1.5833] text-[#273845]">
              {item.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ── Long-form text (legal pages, SEO pages) ────────────────────────── */

const proseComponents = {
  h3: ({ node, ...p }) => <h3 className="mb-3 mt-8 text-[clamp(19px,1.3333vw,24px)] font-semibold leading-snug text-[#273845] first:mt-0" {...p} />,
  h4: ({ node, ...p }) => <h4 className="mb-2 mt-6 text-[clamp(17px,1.1111vw,20px)] font-semibold text-[#273845]" {...p} />,
  p: ({ node, ...p }) => <p className="mb-4 text-[clamp(16px,1vw,18px)] leading-[1.75] text-[#273845] last:mb-0" {...p} />,
  ul: ({ node, ...p }) => <ul className="mb-4 space-y-2 pl-0" {...p} />,
  ol: ({ node, ...p }) => <ol className="mb-4 list-decimal space-y-2 pl-6 text-[clamp(16px,1vw,18px)] leading-[1.75] text-[#273845]" {...p} />,
  li: ({ node, ordered, ...p }) => (
    <li className="flex items-start gap-3 text-[clamp(16px,1vw,18px)] leading-[1.75] text-[#273845]">
      <span aria-hidden className="mt-[0.7em] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand" />
      <span>{p.children}</span>
    </li>
  ),
  strong: ({ node, ...p }) => <strong className="font-semibold" {...p} />,
  a: ({ node, ...p }) => <a className="font-medium text-brand-ink underline underline-offset-2 hover:text-brand-deep" {...p} />,
  table: ({ node, ...p }) => (
    <div className="mb-4 overflow-x-auto rounded-[14px] border border-brand-line">
      <table className="w-full border-collapse text-left text-[clamp(15px,1vw,18px)]" {...p} />
    </div>
  ),
  th: ({ node, ...p }) => <th className="bg-brand-mint px-4 py-3 font-semibold text-[#273845]" {...p} />,
  td: ({ node, ...p }) => <td className="border-t border-brand-line px-4 py-3 text-[#273845]" {...p} />,
};

export const Prose = ({ text }) => <Markdown content={text} components={proseComponents} className="[overflow-wrap:anywhere]" />;

export const LegalSections = ({ sections, notice }) => (
  <section className="hp-container mt-[clamp(40px,4.4444vw,80px)]">
    <div className="hp-card hp-card-navy mx-auto max-w-[1200px] bg-white px-5 py-8 sm:px-10 sm:py-12 lg:px-[clamp(48px,5.5556vw,100px)] lg:py-[clamp(48px,5vw,90px)]">
      {notice && (
        <p className="mb-10 rounded-[14px] bg-brand-mint px-5 py-4 text-[clamp(15px,0.9444vw,17px)] font-medium text-[#273845]" data-testid="legal-language-notice">
          {notice}
        </p>
      )}
      {sections.map((s, i) => (
        <div key={i} className={i > 0 ? 'mt-[clamp(32px,3.3333vw,60px)] border-t border-brand-line pt-[clamp(32px,3.3333vw,60px)]' : ''}>
          <h2 className="mb-5 text-[clamp(24px,2vw,36px)] font-semibold leading-tight text-[#273845]">{s.heading}</h2>
          <Prose text={s.body} />
        </div>
      ))}
    </div>
  </section>
);

/* ── Accordion (FAQ) ────────────────────────────────────────────────── */

export const Accordion = ({ items, renderAnswer }) => {
  const [open, setOpen] = useState(0);
  return (
    <ul className="space-y-4">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <li
            key={item.id || i}
            className={`rounded-[clamp(16px,1.1111vw,20px)] border transition-shadow ${
              isOpen ? 'border-transparent bg-white shadow-[0_40px_16px_rgba(0,0,0,0.04),0_10px_8px_rgba(0,0,0,0.06),0_2px_2px_rgba(0,0,0,0.02)]' : 'border-[#DBDBDB] bg-white'
            }`}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? -1 : i)}
              aria-expanded={isOpen}
              className="flex w-full items-center gap-4 px-[clamp(18px,2vw,36px)] py-[clamp(18px,1.6667vw,30px)] text-left"
            >
              <span className={`flex-1 text-[clamp(17px,1.2222vw,22px)] font-semibold leading-snug ${isOpen ? 'text-brand-ink' : 'text-[#273845]'}`}>
                {item.question}
              </span>
              <ChevronDown className={`h-6 w-6 flex-shrink-0 text-[#273845] transition-transform ${isOpen ? 'rotate-180 text-brand-ink' : ''}`} />
            </button>
            <div className={`grid transition-[grid-template-rows] duration-300 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
              <div className="overflow-hidden">
                <div className="px-[clamp(18px,2vw,36px)] pb-[clamp(18px,1.6667vw,30px)] text-[clamp(16px,1vw,18px)] leading-[1.75] text-[#273845]">
                  {renderAnswer(item)}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export { Check };

/* ── Centred status / form card (token pages, password reset) ───────── */

export const StatusCard = ({ icon, title, children, testId }) => (
  <section className="hp-container py-[clamp(56px,6.6667vw,120px)]">
    <div
      className="hp-card hp-card-navy mx-auto max-w-[640px] bg-white px-6 py-10 text-center sm:px-12 sm:py-14"
      data-testid={testId}
    >
      {icon && <div className="mb-6 flex justify-center">{icon}</div>}
      {title && <h1 className="text-[clamp(28px,2.2222vw,40px)] font-bold leading-tight text-[#273845]">{title}</h1>}
      <div className="mt-4 text-[clamp(16px,1.1111vw,20px)] leading-[1.7] text-[#273845]">{children}</div>
    </div>
  </section>
);

export const StatusIcon = ({ state }) => {
  const base = 'flex h-[clamp(64px,5vw,90px)] w-[clamp(64px,5vw,90px)] items-center justify-center rounded-full';
  if (state === 'loading') {
    return (
      <span className={`${base} bg-brand-mint`}>
        <span className="h-9 w-9 animate-spin rounded-full border-[3px] border-brand border-t-transparent" />
      </span>
    );
  }
  if (state === 'error') {
    return <span className={`${base} bg-red-50 text-4xl font-bold text-red-500`}>!</span>;
  }
  return (
    <span className={`${base} bg-brand-mint`}>
      <Check className="h-1/2 w-1/2 text-brand-ink" strokeWidth={2.5} />
    </span>
  );
};
